import _ from "lodash";
import React, { useRef, useLayoutEffect, useState, useEffect } from "react";
import classNames from "classnames";

import { TwoLinesOutlined } from "@toddle-edu/ds-icons";
import { useI18n } from "Hooks";
import { useMessageOptions } from "UIComponents/Message/providers/MessageProvider";

import classes from "./UnreadMessageLine.scss";

const UnreadMessageLine = ({
  messageId,
  unreadMessageCount,
  showUnreadMessageButton,
  showSummariseNewMessagesWithAi,
  isAISummariseEnabled = false,
  setShowUnreadMessageButton,
  markConversationAsRead,
  generateSummaryForNewMessages,
}) => {
  const { t } = useI18n();
  const containerRef = useRef();
  const { chatId, conversationId, parentKey } = useMessageOptions();
  const [showSummariesWithAi, setShowSummariesWithAi] = useState(false);

  useLayoutEffect(() => {
    const node = containerRef.current;
    // as soon as the line is visible, mark the conversation as read
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          setShowUnreadMessageButton?.(false);
          markConversationAsRead?.({ [parentKey]: chatId || conversationId });
        }
      },
      { threshold: 0.5 }
    );

    if (node) observer.observe(node);

    return () => {
      if (node) observer.unobserve(node);
      observer.disconnect();
    };
  }, [
    chatId,
    conversationId,
    setShowUnreadMessageButton,
    markConversationAsRead,
    parentKey,
  ]);

  useEffect(() => {
    if (_.gte(unreadMessageCount, 3)) {
      setShowSummariesWithAi(true);
    }
  }, [unreadMessageCount]);

  const showSummariesWithAiButton =
    !showUnreadMessageButton &&
    showSummariesWithAi &&
    showSummariseNewMessagesWithAi;

  const onGenerateSummaryClick = () => {
    setShowSummariesWithAi(false);
    generateSummaryForNewMessages?.({
      [parentKey]: chatId || conversationId,
      messageId,
    });
  };

  return (
    <>
      <div ref={containerRef} className={classes.container} id="">
        <div className={classes.line} />
        <div className={classNames("text-body-3", classes.text)}>
          {t("common:new")}
        </div>
        <div className={classes.line} />
      </div>
      {showSummariesWithAiButton && isAISummariseEnabled ? (
        <div
          className={classes.newMessageButtonContainer}
          onClick={onGenerateSummaryClick}
        >
          <TwoLinesOutlined size="xx-small" variant="success" />
          <div
            className={classNames("text-label-3", classes.summaryWithAiText)}
          >
            {t("common:summarise_new_messages_with_ai")}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default UnreadMessageLine;
